import { useState } from 'react'
import { DollarSign, Zap, ShoppingCart, CheckCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react'

interface APIPricingTier {
  tier: string
  credits: number
  apiCount: number
  examples: string[]
  responseTime: string
  monthlyLimit: number
}

interface PricingTier {
  name: 'basic' | 'pro' | 'enterprise'
  displayName: string
  price: number
  yearlyPrice: number
  features: string[]
  paypalPlanId: string
}

interface PlatformPricing {
  rank: number
  name: string
  displayName: string
  businessModel: string
  pricePoint: string
  subscriptionPrice?: string
  monthlyRevenue: number
  margin: number
  users: number
  revenue: number
  description: string
  tiers: PricingTier[]
}

// Phase 12: Supabase 기반 API 과금 모델 (84개 API)
// 1 크레딧 = $0.01
const apiPricingTiers: APIPricingTier[] = [
  {
    tier: 'Free',
    credits: 0,
    apiCount: 3,
    examples: ['run_integration_check', 'get_server_status', 'get_api_version'],
    responseTime: '< 50ms',
    monthlyLimit: -1,
  },
  {
    tier: 'Basic',
    credits: 1,
    apiCount: 8,
    examples: ['calculate_saju', 'get_fortune', 'analyze_mbti_saju', 'analyze_disc'],
    responseTime: '< 200ms',
    monthlyLimit: 300,
  },
  {
    tier: 'Standard',
    credits: 3,
    apiCount: 30,
    examples: ['analyze_western_astrology', 'analyze_meridian', 'analyze_wealth_finance'],
    responseTime: '< 500ms',
    monthlyLimit: -1,
  },
  {
    tier: 'Premium',
    credits: 5,
    apiCount: 20,
    examples: ['analyze_integrated_health', 'generate_birth_chart', 'search_celebrities'],
    responseTime: '< 2s',
    monthlyLimit: -1,
  },
  {
    tier: 'Enterprise',
    credits: 10,
    apiCount: 13,
    examples: ['generate_ai_image', 'generate_talisman', 'batch_process_data'],
    responseTime: '< 5s',
    monthlyLimit: -1,
  },
]

const platformPricings: PlatformPricing[] = [
  {
    rank: 1,
    name: 'agentumi',
    displayName: 'Agentumi',
    businessModel: 'B2B SaaS (AI 에이전트)',
    pricePoint: '$29-299/월',
    subscriptionPrice: '$299',
    monthlyRevenue: 50000,
    margin: 0.85,
    users: 500,
    revenue: 50000,
    description: 'AI 에이전트 마켓플레이스 - 구독 + 에이전트 판매 수수료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 29,
        yearlyPrice: 290,
        paypalPlanId: 'P-AGENTUMI-BASIC-MONTH',
        features: ['기본 AI 에이전트', '월 100회 호출', '커뮤니티 지원', '기본 분석']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 99,
        yearlyPrice: 990,
        paypalPlanId: 'P-AGENTUMI-PRO-MONTH',
        features: ['고급 AI 에이전트', '월 1,000회 호출', '우선 지원', '고급 분석 + API 접근']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 299,
        yearlyPrice: 2990,
        paypalPlanId: 'P-AGENTUMI-ENT-MONTH',
        features: ['무제한 에이전트', '무제한 호출', '전담 매니저', 'SLA 보장 + 맞춤 통합']
      }
    ]
  },
  {
    rank: 2,
    name: 'coinfundmental',
    displayName: 'CoinFundamental',
    businessModel: 'B2B 교육 + 투자 분석',
    pricePoint: '$19-199/월',
    subscriptionPrice: '$149',
    monthlyRevenue: 30000,
    margin: 0.6,
    users: 600,
    revenue: 30000,
    description: '코인/투자 분석 - 구독 + 프리미엄 분석 수수료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 19,
        yearlyPrice: 190,
        paypalPlanId: 'P-COINFUND-BASIC-MONTH',
        features: ['기본 시장 분석', '일간 리포트', '10개 코인 추적', '커뮤니티 접근']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 49,
        yearlyPrice: 490,
        paypalPlanId: 'P-COINFUND-PRO-MONTH',
        features: ['실시간 알림', '시간별 리포트', '50개 코인 추적', 'API 접근']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 199,
        yearlyPrice: 1990,
        paypalPlanId: 'P-COINFUND-ENT-MONTH',
        features: ['맞춤 보고서', '무제한 코인', 'Webhook 통합', '전담 애널리스트']
      }
    ]
  },
  {
    rank: 3,
    name: 'justunbuilt',
    displayName: 'Just Unbuilt',
    businessModel: 'B2B 교육 (과정)',
    pricePoint: '$24-249/월',
    subscriptionPrice: '$499',
    monthlyRevenue: 25000,
    margin: 0.8,
    users: 100,
    revenue: 25000,
    description: 'VC/창업 교육 - 과정 판매 + 1:1 코칭',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 24,
        yearlyPrice: 240,
        paypalPlanId: 'P-UNBUILT-BASIC-MONTH',
        features: ['1개 프로젝트', '기본 템플릿', '커뮤니티 지원', '월간 워크샵']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 79,
        yearlyPrice: 790,
        paypalPlanId: 'P-UNBUILT-PRO-MONTH',
        features: ['5개 프로젝트', '프리미엄 템플릿', '1:1 멘토링', '주간 워크샵']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 249,
        yearlyPrice: 2490,
        paypalPlanId: 'P-UNBUILT-ENT-MONTH',
        features: ['무제한 프로젝트', '맞춤 솔루션', '전담 코치', 'VC 네트워킹']
      }
    ]
  },
  {
    rank: 4,
    name: 'youtubegenius',
    displayName: 'YouTube Genius',
    businessModel: 'B2C SaaS (자동생성)',
    pricePoint: '$22-219/월',
    subscriptionPrice: '$79',
    monthlyRevenue: 25000,
    margin: 0.75,
    users: 2000,
    revenue: 25000,
    description: 'YouTube 콘텐츠 자동 생성 - 월정액 + API 사용료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 22,
        yearlyPrice: 220,
        paypalPlanId: 'P-YTGENIUS-BASIC-MONTH',
        features: ['1개 채널 분석', '월 10개 영상', '기본 SEO', '자동 자막']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 69,
        yearlyPrice: 690,
        paypalPlanId: 'P-YTGENIUS-PRO-MONTH',
        features: ['3개 채널', '월 50개 영상', '고급 SEO', '경쟁사 분석']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 219,
        yearlyPrice: 2190,
        paypalPlanId: 'P-YTGENIUS-ENT-MONTH',
        features: ['무제한 채널', '무제한 영상', 'API 접근', '맞춤 AI 모델']
      }
    ]
  },
  {
    rank: 5,
    name: 'waveletica',
    displayName: 'Waveletica',
    businessModel: 'B2B 크라우드펀딩',
    pricePoint: '$14-144/월',
    monthlyRevenue: 15000,
    margin: 0.75,
    users: 50,
    revenue: 15000,
    description: 'SUI 크라우드펀드 - 펀딩액 커미션',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 14,
        yearlyPrice: 140,
        paypalPlanId: 'P-WAVELETICA-BASIC-MONTH',
        features: ['10곡/월 생성', '기본 장르', 'MP3 다운로드', '커뮤니티 라이센스']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 44,
        yearlyPrice: 440,
        paypalPlanId: 'P-WAVELETICA-PRO-MONTH',
        features: ['50곡/월', '모든 장르', 'WAV 다운로드', '상업 라이센스']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 144,
        yearlyPrice: 1440,
        paypalPlanId: 'P-WAVELETICA-ENT-MONTH',
        features: ['무제한 곡', '맞춤 AI 모델', 'API 접근', '독점 라이센스']
      }
    ]
  },
  {
    rank: 6,
    name: 'goldsaju',
    displayName: 'Gold Saju',
    businessModel: 'B2C 구독 (운세)',
    pricePoint: '$9-99/월',
    subscriptionPrice: '$19',
    monthlyRevenue: 12000,
    margin: 0.6,
    users: 1200,
    revenue: 12000,
    description: '연예인 궁합 분석 - 월정액 구독',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 9,
        yearlyPrice: 90,
        paypalPlanId: 'P-GOLDSAJU-BASIC-MONTH',
        features: ['월 10회 상담', '기본 사주 분석', '궁합 보기', '운세 알림']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 29,
        yearlyPrice: 290,
        paypalPlanId: 'P-GOLDSAJU-PRO-MONTH',
        features: ['무제한 상담', '상세 사주 분석', '연예인 궁합', '맞춤 조언']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 99,
        yearlyPrice: 990,
        paypalPlanId: 'P-GOLDSAJU-ENT-MONTH',
        features: ['API 접근', '대량 분석', '비즈니스 통합', '우선 지원']
      }
    ]
  },
  {
    rank: 7,
    name: 'hahaostory',
    displayName: 'Hahao Story',
    businessModel: 'B2C 구독 (공방)',
    pricePoint: '$11-114/월',
    subscriptionPrice: '$49',
    monthlyRevenue: 10000,
    margin: 0.7,
    users: 400,
    revenue: 10000,
    description: '공방 세계관 구축 - 월정액 + 컨텐츠 판매',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 11,
        yearlyPrice: 110,
        paypalPlanId: 'P-HAHAOSTORY-BASIC-MONTH',
        features: ['20개 스토리/월', '기본 템플릿', '텍스트 전용', '커뮤니티 공유']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 34,
        yearlyPrice: 340,
        paypalPlanId: 'P-HAHAOSTORY-PRO-MONTH',
        features: ['100개 스토리/월', '프리미엄 템플릿', '이미지 생성', '상업 이용']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 114,
        yearlyPrice: 1140,
        paypalPlanId: 'P-HAHAOSTORY-ENT-MONTH',
        features: ['무제한 스토리', 'API 접근', '맞춤 AI', '독점 라이센스']
      }
    ]
  },
  {
    rank: 8,
    name: 'globalbusan',
    displayName: 'Global Busan',
    businessModel: 'B2C 크라우드펀딩',
    pricePoint: '$12-129/월',
    monthlyRevenue: 10000,
    margin: 0.7,
    users: 40,
    revenue: 10000,
    description: '지역 프로젝트 펀딩 - 펀딩액 수수료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 12,
        yearlyPrice: 120,
        paypalPlanId: 'P-GLOBALBUSAN-BASIC-MONTH',
        features: ['소규모 비즈니스', '기본 마케팅', '한국어 지원', '월간 리포트']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 39,
        yearlyPrice: 390,
        paypalPlanId: 'P-GLOBALBUSAN-PRO-MONTH',
        features: ['다국어 지원', '고급 마케팅', 'SNS 통합', '주간 리포트']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 129,
        yearlyPrice: 1290,
        paypalPlanId: 'P-GLOBALBUSAN-ENT-MONTH',
        features: ['전담 매니저', '맞춤 캠페인', 'API 접근', '일일 리포트']
      }
    ]
  },
  {
    rank: 9,
    name: 'monaxio',
    displayName: 'Monaxio',
    businessModel: 'B2B SaaS (팀관리)',
    pricePoint: '$8-79/월',
    subscriptionPrice: '$99',
    monthlyRevenue: 8000,
    margin: 0.6,
    users: 160,
    revenue: 8000,
    description: '감정기반 팀 관리 - 월정액 구독',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 8,
        yearlyPrice: 80,
        paypalPlanId: 'P-MONAXIO-BASIC-MONTH',
        features: ['기본 개발 도구', '테스트넷', '커뮤니티 지원', '문서 접근']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 24,
        yearlyPrice: 240,
        paypalPlanId: 'P-MONAXIO-PRO-MONTH',
        features: ['고급 도구', '테스트넷 우선', 'API 접근', '기술 지원']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 79,
        yearlyPrice: 790,
        paypalPlanId: 'P-MONAXIO-ENT-MONTH',
        features: ['메인넷 배포', '무제한 API', 'SLA 보장', '전담 엔지니어']
      }
    ]
  },
  {
    rank: 10,
    name: 'metacivia',
    displayName: 'Metacivia',
    businessModel: 'B2C 커뮤니티',
    pricePoint: '$8-79/월',
    subscriptionPrice: '$19',
    monthlyRevenue: 8000,
    margin: 0.6,
    users: 800,
    revenue: 8000,
    description: '팬덤 도시 생성 - 커뮤니티 수수료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 8,
        yearlyPrice: 80,
        paypalPlanId: 'P-METACIVIA-BASIC-MONTH',
        features: ['1개 공간', '100명 수용', '기본 빌더', '커뮤니티 지원']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 24,
        yearlyPrice: 240,
        paypalPlanId: 'P-METACIVIA-PRO-MONTH',
        features: ['5개 공간', '1,000명 수용', '고급 빌더', 'NFT 통합']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 79,
        yearlyPrice: 790,
        paypalPlanId: 'P-METACIVIA-ENT-MONTH',
        features: ['무제한 공간', '무제한 수용', 'API 접근', '맞춤 개발']
      }
    ]
  },
  {
    rank: 11,
    name: 'mixue',
    displayName: 'Mixue',
    businessModel: 'B2C 크라우드펀딩',
    pricePoint: '$15-159/월',
    monthlyRevenue: 12000,
    margin: 0.7,
    users: 30,
    revenue: 12000,
    description: '베트남 전기차충전소 - 펀딩액 커미션',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 15,
        yearlyPrice: 150,
        paypalPlanId: 'P-MIXUE-BASIC-MONTH',
        features: ['1개 매장', '기본 마케팅', '재고 관리', '월간 리포트']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 49,
        yearlyPrice: 490,
        paypalPlanId: 'P-MIXUE-PRO-MONTH',
        features: ['5개 매장', 'SNS 통합', 'POS 연동', '주간 리포트']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 159,
        yearlyPrice: 1590,
        paypalPlanId: 'P-MIXUE-ENT-MONTH',
        features: ['프랜차이즈 관리', 'API 접근', '맞춤 통합', '전담 매니저']
      }
    ]
  },
  {
    rank: 12,
    name: 'sui100',
    displayName: 'Sui100',
    businessModel: 'B2B 에코시스템',
    pricePoint: '$8-79/월',
    subscriptionPrice: '$299',
    monthlyRevenue: 8000,
    margin: 0.6,
    users: 80,
    revenue: 8000,
    description: 'SUI 생태계 토큰 - API 접근료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 8,
        yearlyPrice: 80,
        paypalPlanId: 'P-SUI100-BASIC-MONTH',
        features: ['기본 지갑', '10개 토큰', '커뮤니티 지원', '거래 추적']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 24,
        yearlyPrice: 240,
        paypalPlanId: 'P-SUI100-PRO-MONTH',
        features: ['DEX 접근', '100개 토큰', 'API 접근', '고급 분석']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 79,
        yearlyPrice: 790,
        paypalPlanId: 'P-SUI100-ENT-MONTH',
        features: ['무제한 토큰', 'Webhook', '맞춤 통합', '우선 지원']
      }
    ]
  },
  {
    rank: 13,
    name: 'hahaotool',
    displayName: 'Hahao Tool',
    businessModel: 'B2C SaaS (도구)',
    pricePoint: '$5-52/월',
    subscriptionPrice: '$9',
    monthlyRevenue: 5000,
    margin: 0.6,
    users: 1000,
    revenue: 5000,
    description: '공방 필수 도구 모음 - 월정액 구독',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 5,
        yearlyPrice: 50,
        paypalPlanId: 'P-HAHAOTOOL-BASIC-MONTH',
        features: ['5개 도구', '기본 기능', '광고 포함', '커뮤니티 지원']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 16,
        yearlyPrice: 160,
        paypalPlanId: 'P-HAHAOTOOL-PRO-MONTH',
        features: ['20개 도구', '고급 기능', '광고 없음', '우선 지원']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 52,
        yearlyPrice: 520,
        paypalPlanId: 'P-HAHAOTOOL-ENT-MONTH',
        features: ['모든 도구', 'API 접근', '맞춤 도구', '전담 지원']
      }
    ]
  },
  {
    rank: 14,
    name: 'hahaoplace',
    displayName: 'Hahao Place',
    businessModel: 'B2C 정보 서비스',
    pricePoint: '$7-69/월',
    subscriptionPrice: '$9',
    monthlyRevenue: 6000,
    margin: 0.6,
    users: 1200,
    revenue: 6000,
    description: '공방 정보 데이터베이스 - 월정액',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 7,
        yearlyPrice: 70,
        paypalPlanId: 'P-HAHAOPLACE-BASIC-MONTH',
        features: ['10개 장소 등록', '기본 정보', '커뮤니티 접근', '월간 업데이트']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 21,
        yearlyPrice: 210,
        paypalPlanId: 'P-HAHAOPLACE-PRO-MONTH',
        features: ['50개 장소', '상세 정보', 'SEO 최적화', '주간 업데이트']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 69,
        yearlyPrice: 690,
        paypalPlanId: 'P-HAHAOPLACE-ENT-MONTH',
        features: ['무제한 장소', 'API 접근', '맞춤 통합', '실시간 업데이트']
      }
    ]
  },
  {
    rank: 15,
    name: 'deomz',
    displayName: 'Deomz',
    businessModel: 'B2C 소상공인 지원',
    pricePoint: '$7-69/월',
    subscriptionPrice: '$59',
    monthlyRevenue: 6000,
    margin: 0.6,
    users: 200,
    revenue: 6000,
    description: '시장 운영 플랫폼 - 월정액 + 거래 수수료',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 7,
        yearlyPrice: 70,
        paypalPlanId: 'P-DEOMZ-BASIC-MONTH',
        features: ['5개 데모', '기본 템플릿', '링크 공유', '커뮤니티 지원']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 21,
        yearlyPrice: 210,
        paypalPlanId: 'P-DEOMZ-PRO-MONTH',
        features: ['20개 데모', '고급 템플릿', '분석 도구', '우선 지원']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 69,
        yearlyPrice: 690,
        paypalPlanId: 'P-DEOMZ-ENT-MONTH',
        features: ['무제한 데모', 'API 접근', '화이트라벨', '전담 매니저']
      }
    ]
  },
  {
    rank: 16,
    name: 'kaching',
    displayName: 'Kaching',
    businessModel: 'B2C 소상공인 팁',
    pricePoint: '$5-49/월',
    subscriptionPrice: '$29',
    monthlyRevenue: 4000,
    margin: 0.5,
    users: 200,
    revenue: 4000,
    description: '글로벌 소상공인 정보 - 월정액 구독',
    tiers: [
      {
        name: 'basic',
        displayName: 'Basic',
        price: 5,
        yearlyPrice: 50,
        paypalPlanId: 'P-KACHING-BASIC-MONTH',
        features: ['100건 거래/월', '기본 결제', '1% 수수료', '커뮤니티 지원']
      },
      {
        name: 'pro',
        displayName: 'Pro',
        price: 14,
        yearlyPrice: 140,
        paypalPlanId: 'P-KACHING-PRO-MONTH',
        features: ['1,000건 거래/월', '고급 결제', '0.5% 수수료', '우선 지원']
      },
      {
        name: 'enterprise',
        displayName: 'Enterprise',
        price: 49,
        yearlyPrice: 490,
        paypalPlanId: 'P-KACHING-ENT-MONTH',
        features: ['무제한 거래', 'API 접근', '0.3% 수수료', 'SLA 보장']
      }
    ]
  },
]

export default function PricingPage() {
  const [activeTab, setActiveTab] = useState<'api' | 'platform'>('api')
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null)

  const handleSubscribe = async (platformName: string, tierName?: 'basic' | 'pro' | 'enterprise') => {
    try {
      setIsLoading(true)
      setCheckoutError(null)
      setSelectedPlan(`${platformName}-${tierName || 'pro'}`)

      // Use mcp-saju-realserver API endpoint
      const apiBase = window.location.hostname === 'localhost' 
        ? 'http://localhost:8787' 
        : 'https://api-realserver.goldsaju.com'
      
      const returnUrl = `${window.location.origin}/dashboard?payment=success&platform=${platformName}`
      const cancelUrl = `${window.location.origin}/pricing?canceled=true`

      // Get user info (mock for now - replace with actual auth)
      const userId = 'user_' + Date.now()
      const userEmail = 'user@example.com' // Replace with actual user email from auth

      // Get platform and tier details
      const platform = platformPricings.find(p => p.name === platformName)
      if (!platform) {
        throw new Error('Platform not found')
      }

      const tier = tierName || 'pro'
      const tierData = platform.tiers.find(t => t.name === tier)
      if (!tierData) {
        throw new Error('Tier not found')
      }

      const response = await fetch(`${apiBase}/api/billing/paypal/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platformId: platformName,
          planType: tier,
          amount: tierData.price.toString(),
          currency: 'USD',
          returnUrl,
          cancelUrl,
          userId,
          userEmail,
        }),
      })

      const data = await response.json() as any

      if (data.success && data.data?.approvalUrl) {
        // Redirect to PayPal approval page
        window.location.href = data.data.approvalUrl
      } else {
        setCheckoutError(data.error || '결제 주문 생성에 실패했습니다. 다시 시도해주세요.')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      setCheckoutError('결제 처리 중 오류가 발생했습니다. 네트워크 연결을 확인하세요.')
    } finally {
      setIsLoading(false)
      setSelectedPlan(null)
    }
  }

  const togglePlatform = (platformName: string) => {
    setExpandedPlatform(expandedPlatform === platformName ? null : platformName)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg p-6">
        <h1 className="text-3xl font-bold text-white mb-2">💰 과금 및 수익구조</h1>
        <p className="text-gray-300">mcp.goldsaju.com API 가격표 및 16개 플랫폼 수익 모델</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-4">
        <button
          onClick={() => setActiveTab('api')}
          className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
            activeTab === 'api'
              ? 'bg-sky-600 text-white'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          <Zap className="inline h-5 w-5 mr-2" />
          API 과금률표
        </button>
        <button
          onClick={() => setActiveTab('platform')}
          className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
            activeTab === 'platform'
              ? 'bg-sky-600 text-white'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          <DollarSign className="inline h-5 w-5 mr-2" />
          플랫폼 수익구조
        </button>
      </div>

      {/* API Pricing */}
      {activeTab === 'api' && (
        <div className="space-y-4">
          <div className="bg-slate-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-2">🔌 mcp.goldsaju.com API 가격표</h2>
            <p className="text-gray-400 text-sm mb-6">Phase 12: 84개 API, 크레딧 기반 요금 모델 (1 크레딧 = $0.01)</p>
            
            <div className="space-y-4">
              {apiPricingTiers.map((tier, idx) => {
                const costPerCall = tier.credits * 0.01
                
                return (
                  <div key={idx} className="border border-slate-700 rounded-lg p-4 hover:bg-slate-700/50 transition">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      {/* Tier */}
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider">Tier</p>
                        <p className={`text-xl font-bold ${
                          tier.tier === 'Free' ? 'text-green-400' :
                          tier.tier === 'Basic' ? 'text-blue-400' :
                          tier.tier === 'Standard' ? 'text-purple-400' :
                          tier.tier === 'Premium' ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {tier.tier}
                        </p>
                      </div>

                      {/* Credits */}
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider">크레딧</p>
                        <p className="text-xl font-bold text-white">{tier.credits}</p>
                        <p className="text-xs text-gray-500">${costPerCall.toFixed(3)}/요청</p>
                      </div>

                      {/* API Count */}
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider">API 개수</p>
                        <p className="text-xl font-bold text-cyan-400">{tier.apiCount}개</p>
                        <p className="text-xs text-gray-500">총 84개 중</p>
                      </div>

                      {/* Performance */}
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider">응답 시간</p>
                        <p className="text-sm text-white font-semibold">{tier.responseTime}</p>
                        <p className="text-xs text-gray-500">{
                          tier.monthlyLimit === -1 ? '무제한' : `${tier.monthlyLimit}/월`
                        }</p>
                      </div>

                      {/* Examples */}
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider">예시</p>
                        <div className="space-y-1">
                          {tier.examples.slice(0, 2).map((ex, i) => (
                            <p key={i} className="text-xs text-gray-300 truncate">{ex}</p>
                          ))}
                          {tier.examples.length > 2 && (
                            <p className="text-xs text-gray-500">+{tier.examples.length - 2} more</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* API Summary */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">총 API 개수</p>
                <p className="text-2xl font-bold text-sky-400">84</p>
              </div>
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">가장 저렴</p>
                <p className="text-2xl font-bold text-green-400">
                  Free (0)
                </p>
              </div>
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">가장 비쌈</p>
                <p className="text-2xl font-bold text-orange-400">
                  Enterprise (10cr = $0.10)
                </p>
              </div>
            </div>

            {/* Credit Plan */}
            <div className="mt-6 bg-slate-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">💳 월간 구독 플랜</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800 rounded p-3">
                  <p className="text-xs text-gray-400">Starter</p>
                  <p className="text-lg font-bold text-white">$9.99/월</p>
                  <p className="text-xs text-cyan-400">1,200 크레딧</p>
                </div>
                <div className="bg-slate-800 rounded p-3">
                  <p className="text-xs text-gray-400">Professional</p>
                  <p className="text-lg font-bold text-white">$29.99/월</p>
                  <p className="text-xs text-cyan-400">4,000 크레딧</p>
                </div>
                <div className="bg-slate-800 rounded p-3">
                  <p className="text-xs text-gray-400">Business</p>
                  <p className="text-lg font-bold text-white">$99.99/월</p>
                  <p className="text-xs text-cyan-400">15,000 크레딧</p>
                </div>
                <div className="bg-slate-800 rounded p-3">
                  <p className="text-xs text-gray-400">Enterprise</p>
                  <p className="text-lg font-bold text-white">협의</p>
                  <p className="text-xs text-cyan-400">무제한</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Pricing */}
      {activeTab === 'platform' && (
        <div className="space-y-4">
          {checkoutError && (
            <div className="bg-red-900 border border-red-700 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-200">결제 오류</p>
                <p className="text-red-300 text-sm">{checkoutError}</p>
              </div>
            </div>
          )}
          
          <div className="bg-slate-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">🎯 16개 플랫폼 구독 플랜</h2>
            <p className="text-gray-400 mb-6">각 플랫폼을 클릭하여 Basic, Pro, Enterprise 플랜을 확인하세요</p>
            
            <div className="space-y-3">
              {platformPricings.map((platform, idx) => (
                <div key={idx} className="border border-slate-700 rounded-lg overflow-hidden">
                  {/* Platform Header */}
                  <div 
                    className="bg-slate-700/50 hover:bg-slate-700 transition-colors cursor-pointer p-4 flex items-center justify-between"
                    onClick={() => togglePlatform(platform.name)}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <span className={`font-bold text-lg ${
                        idx < 3 ? 'text-yellow-400' : idx < 6 ? 'text-orange-400' : 'text-gray-400'
                      }`}>
                        #{platform.rank}
                      </span>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-lg">{platform.displayName}</h3>
                        <p className="text-gray-400 text-sm">{platform.businessModel}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-semibold">{platform.pricePoint}</p>
                        <p className="text-gray-400 text-sm">${platform.revenue.toLocaleString()}/월</p>
                      </div>
                      <div className="text-right">
                        <span className={`font-semibold text-sm px-3 py-1 rounded ${
                          platform.margin >= 0.7 ? 'bg-green-900 text-green-300' :
                          platform.margin >= 0.6 ? 'bg-yellow-900 text-yellow-300' : 'bg-orange-900 text-orange-300'
                        }`}>
                          {(platform.margin * 100).toFixed(0)}% 마진
                        </span>
                      </div>
                      {expandedPlatform === platform.name ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Pricing Tiers */}
                  {expandedPlatform === platform.name && (
                    <div className="bg-slate-800 p-6">
                      <p className="text-gray-300 mb-4 text-sm">{platform.description}</p>
                      <div className="grid grid-cols-3 gap-4">
                        {platform.tiers.map((tier) => (
                          <div 
                            key={tier.name}
                            className={`border rounded-lg p-4 transition-all ${
                              tier.name === 'pro' 
                                ? 'border-sky-500 bg-sky-950/30 shadow-lg shadow-sky-500/20' 
                                : 'border-slate-600 bg-slate-700/30'
                            }`}
                          >
                            {tier.name === 'pro' && (
                              <div className="bg-sky-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                                MOST POPULAR
                              </div>
                            )}
                            <h4 className="text-white font-bold text-lg mb-2">{tier.displayName}</h4>
                            <div className="mb-4">
                              <span className="text-3xl font-bold text-white">${tier.price}</span>
                              <span className="text-gray-400">/월</span>
                              <p className="text-gray-500 text-xs mt-1">
                                연간 ${tier.yearlyPrice} (17% 할인)
                              </p>
                            </div>
                            <ul className="space-y-2 mb-4">
                              {tier.features.map((feature, fIdx) => (
                                <li key={fIdx} className="text-gray-300 text-sm flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSubscribe(platform.name, tier.name)
                              }}
                              disabled={isLoading && selectedPlan === `${platform.name}-${tier.name}`}
                              className={`w-full font-bold py-3 px-4 rounded transition-colors ${
                                tier.name === 'pro'
                                  ? 'bg-sky-500 hover:bg-sky-600 text-white'
                                  : 'bg-slate-600 hover:bg-slate-500 text-white'
                              } disabled:bg-gray-500 disabled:cursor-not-allowed`}
                            >
                              {isLoading && selectedPlan === `${platform.name}-${tier.name}` ? (
                                <span>처리중...</span>
                              ) : (
                                <span className="flex items-center justify-center gap-2">
                                  <ShoppingCart className="w-4 h-4" />
                                  구독하기
                                </span>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Platform Summary */}
            <div className="mt-6 grid grid-cols-4 gap-4">
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">총 월 매출</p>
                <p className="text-2xl font-bold text-green-400">
                  ${platformPricings.reduce((sum, p) => sum + p.revenue, 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">평균 마진율</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {(platformPricings.reduce((sum, p) => sum + p.margin, 0) / platformPricings.length * 100).toFixed(1)}%
                </p>
              </div>
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">총 순수익</p>
                <p className="text-2xl font-bold text-lime-400">
                  ${platformPricings.reduce((sum, p) => sum + (p.revenue * p.margin), 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">총 사용자</p>
                <p className="text-2xl font-bold text-sky-400">
                  {platformPricings.reduce((sum, p) => sum + p.users, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-2">📊 주요 지표</h3>
        <div className="grid grid-cols-2 gap-4 text-sm text-gray-300">
          <div>
            <p className="font-semibold text-white mb-1">수익 모델 분류:</p>
            <ul className="space-y-1">
              <li>• SaaS: 월정액 구독</li>
              <li>• 과정: 일회성 구매</li>
              <li>• 크라우드펀딩: 펀딩액 수수료</li>
              <li>• 하이브리드: 복합 수익구조</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-white mb-1">마진율 해석:</p>
            <ul className="space-y-1">
              <li>🟢 70%+: 우수 (SaaS/교육)</li>
              <li>🟡 60-70%: 양호 (구독)</li>
              <li>🟠 50-60%: 보통 (크라우드펀딩)</li>
              <li>🔴 50% 이하: 개선필요</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
